/**
 * User Role Operations Service - Phase 4 Architecture
 *
 * Encapsulates user role assignment, removal, and updates.
 * Extracted from UserService to reduce single-responsibility violations.
 */

import type { Database } from "@/integrations/supabase/database.types";
import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import type { IService } from "@/services/core/service-registry";
import { ServiceRegistry } from "@/services/core/service-registry";
import { AuditService } from "./auditService";

export class UserRoleOperationsService implements IService {
  readonly name = "user-role-operations" as const;
  private readonly supabase = typedSupabase;
  private auditService?: AuditService;
  private registry: ServiceRegistry;

  constructor(serviceRegistry: ServiceRegistry) {
    this.registry = serviceRegistry;
  }

  async initialize(): Promise<void> {
    logger.info("🔑 UserRoleOperationsService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("🔑 UserRoleOperationsService disposed");
  }

  private async ensureAudit(): Promise<AuditService> {
    this.auditService ??= await this.registry.get<AuditService>("audit");
    return this.auditService;
  }

  async updateUserRole(userId: string, role: string): Promise<void> {
    type AppRole = Database["public"]["Enums"]["app_role"];

    const { data: roleData, error: roleError } = await this.supabase
      .from("user_roles")
      .select("*")
      .eq("user_id", userId)
      .eq("role", role as AppRole)
      .single();

    if (roleError && roleError.code !== "PGRST116") {
      throw new Error(`Failed to check user role: ${roleError.message}`);
    }

    if (roleData) {
      logger.info(`User ${userId} already has role ${role}`);
      return;
    }

    const { error: insertError } = await this.supabase
      .from("user_roles")
      .insert({ user_id: userId, role: role as AppRole });

    if (insertError) {
      throw new Error(`Failed to insert user role: ${insertError.message}`);
    }

    try {
      await (await this.ensureAudit()).logAdminAction({
        action: "update_user_role",
        entity_type: "user",
        entity_id: userId,
        details: { role_assigned: role },
      });
    } catch (auditErr) {
      logger.warn("Audit log failed for updateUserRole", { userId, error: auditErr });
    }
  }

  async assignRole(
    userId: string,
    role: string
  ): Promise<{ success: boolean; message?: string }> {
    type AppRole = Database["public"]["Enums"]["app_role"];
    const { error } = await this.supabase
      .from("user_roles")
      .insert([{ user_id: userId, role: role as AppRole }]);

    if (error) {
      if (error.code === "23505") {
        return { success: false, message: "User already has this role." };
      }
      throw new Error(`Failed to assign role: ${error.message}`);
    }

    try {
      await (await this.ensureAudit()).logAdminAction({
        action: "assign_user_role",
        entity_type: "user_role",
        entity_id: userId,
        details: { role_assigned: role },
      });
    } catch (auditErr) {
      logger.warn("Audit log failed for assignRole", { userId, error: auditErr });
    }

    return { success: true };
  }

  async removeRole(
    roleId: string,
    userId: string
  ): Promise<{ success: boolean; message?: string }> {
    const { error } = await this.supabase
      .from("user_roles")
      .delete()
      .eq("id", roleId)
      .eq("user_id", userId);

    if (error) {
      throw new Error(`Failed to remove role: ${error.message}`);
    }

    try {
      await (await this.ensureAudit()).logAdminAction({
        action: "remove_user_role",
        entity_type: "user_role",
        entity_id: userId,
        details: { role_id_removed: roleId },
      });
    } catch (auditErr) {
      logger.warn("Audit log failed for removeRole", { userId, error: auditErr });
    }

    return { success: true };
  }
}
